import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, mergeMap } from 'rxjs/operators';
import * as ImageActions from './image.actions';
import { ImageService } from '../../services/image.service';
import { NotificationService } from '../../services/notification.service';
import { TranslateService } from '@ngx-translate/core';

export const uploadImage$ = createEffect(
  (
    actions$ = inject(Actions),
    imageService = inject(ImageService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(ImageActions.uploadImage),
      mergeMap(({ formData }) =>
        imageService.uploadImage(formData).pipe(
          map((res) => {
            notification.success(translate.instant('image.uploadSuccess'));
            return ImageActions.uploadImageSuccess({
              imageUrl: res.imageUrl,
              publicId: res.publicId
            });
          }),
          catchError((error) => {
            notification.error(translate.instant('image.uploadFailure'));
            return of(ImageActions.uploadImageFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const deleteImage$ = createEffect(
  (
    actions$ = inject(Actions),
    imageService = inject(ImageService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(ImageActions.deleteImage),
      mergeMap(({ publicId }) =>
        imageService.deleteImage(publicId).pipe(
          map((res) => {
            notification.success(translate.instant('image.deleteSuccess'));
            return ImageActions.deleteImageSuccess({ result: res.result });
          }),
          catchError((error) => {
            notification.error(translate.instant('image.deleteFailure'));
            return of(ImageActions.deleteImageFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const generateRecipeImage$ = createEffect(
  (
    actions$ = inject(Actions),
    imageService = inject(ImageService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(ImageActions.generateRecipeImage),
      // Ignore duplicate clicks while a paid generation request is running.
      exhaustMap(({ context }) =>
        imageService.generateRecipeImage(context).pipe(
          map((res) => {
            notification.success(translate.instant('image.generateSuccess'));
            return ImageActions.generateRecipeImageSuccess({
              imageUrl: res.imageUrl,
              publicId: res.publicId
            });
          }),
          catchError((error) => {
            notification.error(translate.instant('image.generateFailure'));
            return of(ImageActions.generateRecipeImageFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const ImageEffects = {
  uploadImage$,
  deleteImage$,
  generateRecipeImage$,
};
